// frontend/src/components/widgets/WebContentConfig.js - WITH PRESETS + THEMES + PREVIEW
import React, { useState, useEffect } from 'react';
import { Globe, TestTube, Check, X, AlertCircle, RefreshCw, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WebContentConfig = ({ config, onChange, onSave, onCancel, existingConfig }) => {
  const { t } = useTranslation();
  
  // ✅ PRESET DEFINITIONS
  const WEB_CONTENT_PRESETS = {
    dashboard: {
      displayMode: 'auto',
      refreshInterval: 300,
      zoom: 100,
      scrollPosition: 'top',
      theme: 'light'
    },
    news: {
      displayMode: 'screenshot',
      refreshInterval: 600,
      zoom: 90,
      scrollPosition: 'top',
      theme: 'dark'
    },
    monitor: {
      displayMode: 'iframe',
      refreshInterval: 180,
      zoom: 100,
      scrollPosition: 'top',
      theme: 'blue'
    }
  };

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState('custom');

  const [formData, setFormData] = useState({
    name: existingConfig?.name || '',
    url: existingConfig?.config?.url || config?.url || '',
    displayMode: existingConfig?.config?.displayMode || config?.displayMode || 'auto',
    refreshInterval: existingConfig?.config?.refreshInterval || config?.refreshInterval || 300,
    zoom: existingConfig?.config?.zoom || config?.zoom || 100,
    scrollPosition: existingConfig?.config?.scrollPosition || config?.scrollPosition || 'top',
    theme: existingConfig?.config?.theme || config?.theme || 'light'
  });

  // ✅ Update formData when existingConfig changes (for edit mode)
  useEffect(() => {
    console.log('🔍 WebContentConfig - existingConfig:', existingConfig);
    if (existingConfig) {
      console.log('✅ Updating formData with:', {
        name: existingConfig.name,
        url: existingConfig.config?.url
      });
      setFormData({
        name: existingConfig.name || '',
        url: existingConfig.config?.url || '',
        displayMode: existingConfig.config?.displayMode || 'auto',
        refreshInterval: existingConfig.config?.refreshInterval || 300,
        zoom: existingConfig.config?.zoom || 100,
        scrollPosition: existingConfig.config?.scrollPosition || 'top',
        theme: existingConfig.config?.theme || 'light'
      });
    }
  }, [existingConfig]);

  // ✅ PRESET HANDLER
  const handlePresetChange = (presetKey) => {
    if (presetKey === 'custom') {
      setSelectedPreset('custom');
      return;
    }

    const preset = WEB_CONTENT_PRESETS[presetKey];
    if (preset) {
      setFormData(prev => ({
        ...prev,
        ...preset
      }));
      setSelectedPreset(presetKey);
    }
  };

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    setSelectedPreset('custom');
    if (onChange) {
      onChange(updated);
    }
  };

  const testIframeCompatibility = async () => {
    if (!formData.url) {
      alert('Please enter a URL first');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api';
      
      const response = await fetch(`${apiUrl}/web-content/test-iframe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: formData.url })
      });

      if (!response.ok) {
        throw new Error('Test failed');
      }

      const result = await response.json();
      setTestResult(result);

      // Auto-set display mode based on test result
      if (formData.displayMode === 'auto') {
        const recommendedMode = result.isEmbeddable ? 'iframe' : 'screenshot';
        handleChange('displayMode', recommendedMode);
      }

    } catch (error) {
      console.error('Test error:', error);
      setTestResult({ 
        error: true, 
        errorMessage: 'Failed to test URL' 
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.url) {
      alert(t('common.required') || 'Name and URL are required');
      return;
    }

    onSave({
      name: formData.name,
      config: {
        url: formData.url,
        displayMode: formData.displayMode,
        refreshInterval: formData.refreshInterval,
        zoom: formData.zoom,
        scrollPosition: formData.scrollPosition,
        theme: formData.theme
      }
    });
  };

  // ✅ PRESET OPTIONS
  const presetOptions = [
    { value: 'custom', label: t('widgets.webContent.presetCustom') || 'Custom' },
    { value: 'dashboard', label: t('widgets.webContent.presetDashboard') || 'Dashboard', desc: t('widgets.webContent.presetDashboardDesc') || 'General web dashboard' },
    { value: 'news', label: t('widgets.webContent.presetNews') || 'News Site', desc: t('widgets.webContent.presetNewsDesc') || 'News & updates' },
    { value: 'monitor', label: t('widgets.webContent.presetMonitor') || 'Live Monitor', desc: t('widgets.webContent.presetMonitorDesc') || 'Real-time monitoring' }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-3 rounded-lg">
            <Globe size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('widgets.webContent.configTitle') || 'Web Content Widget Configuration'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('widgets.webContent.subtitle') || 'Display websites and web applications'}
            </p>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      {/* Content */}
      <div className="p-6 max-h-[70vh] overflow-y-auto space-y-6">
        
        {/* Widget Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.webContent.widgetName') || t('widgets.widgetName')} *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Company Dashboard"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
          />
        </div>

        {/* ✅ PRESET SELECTOR */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Sparkles size={20} />
            {t('widgets.webContent.presets') || 'Presets'}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {presetOptions.map(preset => (
              <button
                key={preset.value}
                type="button"
                onClick={() => handlePresetChange(preset.value)}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  selectedPreset === preset.value
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-700'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {preset.label}
                </div>
                {preset.desc && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {preset.desc}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* URL Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.webContent.url') || 'URL'} *
          </label>
          <div className="flex gap-2">
            <input
              type="url"
              value={formData.url}
              onChange={(e) => handleChange('url', e.target.value)}
              placeholder="https://example.com"
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
            />
            <button
              type="button"
              onClick={testIframeCompatibility}
              disabled={testing || !formData.url}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {testing ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  {t('common.testing')}
                </>
              ) : (
                <>
                  <TestTube size={18} />
                  {t('widgets.webContent.test') || 'Test'}
                </>
              )}
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {t('widgets.webContent.urlHelp') || 'Enter a valid HTTPS URL'}
          </p>
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`p-4 rounded-lg border-2 ${
            testResult.error 
              ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' 
              : testResult.isEmbeddable
              ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
              : 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800'
          }`}>
            <div className="flex items-start gap-3">
              {testResult.error ? (
                <X className="text-red-600 flex-shrink-0 mt-1" size={24} />
              ) : testResult.isEmbeddable ? (
                <Check className="text-green-600 flex-shrink-0 mt-1" size={24} />
              ) : (
                <AlertCircle className="text-orange-600 flex-shrink-0 mt-1" size={24} />
              )}
              <div className="flex-1">
                <h4 className="font-semibold mb-1">
                  {testResult.error 
                    ? 'Test Failed'
                    : testResult.isEmbeddable
                    ? '✅ iframe Compatible'
                    : '⚠️ iframe Blocked'
                  }
                </h4>
                <p className="text-sm opacity-80">
                  {testResult.errorMessage || 
                   (testResult.isEmbeddable 
                     ? 'Website can be embedded with iframe'
                     : 'Screenshot mode recommended'
                   )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Theme Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.webContent.theme') || 'Theme'}
          </label>
          <select
            value={formData.theme}
            onChange={(e) => handleChange('theme', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="light">{t('widgets.webContent.themeLight') || 'Light'}</option>
            <option value="dark">{t('widgets.webContent.themeDark') || 'Dark'}</option>
            <option value="blue">{t('widgets.webContent.themeBlue') || 'Blue (AlpenScreen)'}</option>
            <option value="classicBlue">{t('widgets.webContent.themeClassicBlue') || 'Classic Blue'}</option>
            <option value="green">{t('widgets.webContent.themeGreen') || 'Green'}</option>
            <option value="minimal">{t('widgets.webContent.themeMinimal') || 'Minimal'}</option>
          </select>
        </div>

        {/* Display Mode */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.webContent.displayMode') || 'Display Mode'}
          </label>
          <select
            value={formData.displayMode}
            onChange={(e) => handleChange('displayMode', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="auto">{t('widgets.webContent.modeAuto') || 'Auto (Recommended)'}</option>
            <option value="iframe">{t('widgets.webContent.modeIframe') || 'iframe Mode'}</option>
            <option value="screenshot">{t('widgets.webContent.modeScreenshot') || 'Screenshot Mode'}</option>
          </select>
          <p className="mt-1 text-sm text-gray-500">
            {formData.displayMode === 'auto' && (t('widgets.webContent.modeAutoHelp') || 'Automatically choose best mode')}
            {formData.displayMode === 'iframe' && (t('widgets.webContent.modeIframeHelp') || 'Embed live website (may be blocked)')}
            {formData.displayMode === 'screenshot' && (t('widgets.webContent.modeScreenshotHelp') || 'Periodic screenshots (always works)')}
          </p>
        </div>

        {/* Refresh Interval */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.webContent.refreshInterval') || 'Refresh Interval'}
          </label>
          <div className="flex items-center gap-4">
            <input
              type="number"
              value={formData.refreshInterval}
              onChange={(e) => handleChange('refreshInterval', parseInt(e.target.value))}
              min="60"
              className="w-32 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
            />
            <span className="text-gray-600 dark:text-gray-400">{t('common.seconds')}</span>
          </div>
        </div>

        {/* Zoom Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.webContent.zoom') || 'Zoom'}
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              value={formData.zoom}
              onChange={(e) => handleChange('zoom', parseInt(e.target.value))}
              min="50"
              max="200"
              step="10"
              className="flex-1"
            />
            <span className="w-16 text-center font-semibold text-gray-700 dark:text-gray-300">
              {formData.zoom}%
            </span>
          </div>
        </div>

        {/* Scroll Position (for screenshots) */}
        {formData.displayMode !== 'iframe' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('widgets.webContent.scrollPosition') || 'Scroll Position'}
            </label>
            <select
              value={formData.scrollPosition}
              onChange={(e) => handleChange('scrollPosition', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="top">Top</option>
              <option value="middle">Middle</option>
              <option value="bottom">Bottom</option>
            </select>
          </div>
        )}

        {/* ✅ VISUAL PREVIEW */}
        {formData.url && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Sparkles size={20} />
              {t('widgets.webContent.preview') || 'Preview'}
            </h3>
            
            <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 rounded-lg overflow-hidden" style={{ height: '300px' }}>
              {/* Mode Badge */}
              <div className="absolute top-4 left-4 z-10 bg-green-600 px-3 py-1 rounded-full text-white text-sm font-bold flex items-center gap-2">
                <Globe size={14} />
                <span>{formData.displayMode === 'auto' ? 'Auto Mode' : formData.displayMode === 'iframe' ? 'iframe' : 'Screenshot'}</span>
              </div>

              {/* Browser Frame */}
              <div className="absolute inset-0 flex flex-col">
                {/* Browser Chrome */}
                <div className="bg-gray-300 dark:bg-gray-700 px-4 py-2 flex items-center gap-2 border-b border-gray-400 dark:border-gray-600">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="flex-1 bg-white dark:bg-gray-800 rounded px-3 py-1 text-xs text-gray-600 dark:text-gray-400 truncate">
                    {formData.url}
                  </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 bg-white dark:bg-gray-900 flex items-center justify-center p-8">
                  <div className="text-center">
                    <Globe size={64} className="mx-auto mb-4 text-gray-400" />
                    <div className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-2">
                      {formData.url.replace(/^https?:\/\//i, '').split('/')[0]}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Zoom: {formData.zoom}% • Refresh: {formData.refreshInterval}s
                    </div>
                  </div>
                </div>
              </div>

              {/* Settings Info */}
              <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm px-3 py-1 rounded text-white text-xs">
                Theme: <strong>{formData.theme}</strong>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onCancel}
          className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-colors font-semibold"
        >
          {existingConfig ? t('common.save') : (t('widgets.webContent.saveWidget') || t('common.save'))}
        </button>
      </div>
    </div>
  );
};

export default WebContentConfig;